import { useId } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatTrialDate } from "@/lib/trialDate";
import { useCancelScheduledSubscription } from "@/hooks/use-teacher-subscription";
import type { ScheduledSubscriptionResponse } from "@/services/teacherSubscriptionService";

/**
 * AC#19 -- "<Tier> starts <date>, when your trial ends", with a way back out.
 *
 * Shown on both the teacher dashboard (where it REPLACES the trial countdown -- a teacher who has
 * already decided does not need a countdown nagging them) and the subscription page. One component
 * for both so the copy, the confirmation dialog and the cancel call cannot drift apart.
 *
 * Cancelling is confirmed, not immediate: it throws away a completed payment mandate, and the
 * dialog is the only place that spells out what the teacher is left with (the trial, then Free).
 */

/** "GOLD" -> "Gold". The tier reads as a product name here, not as an enum. */
function tierTitle(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
}

export interface ScheduledPurchaseBannerProps {
  scheduled: ScheduledSubscriptionResponse;
  /** `trial.endsAt` -- names the date the teacher drops to Free in the success toast. */
  trialEndsAt: string | null;
  className?: string;
}

export function ScheduledPurchaseBanner({ scheduled, trialEndsAt, className }: ScheduledPurchaseBannerProps) {
  const headingId = useId();
  const cancelScheduled = useCancelScheduledSubscription();

  const tier = tierTitle(scheduled.tier);
  const startsOn = formatTrialDate(scheduled.startsOn);

  return (
    <div
      role="status"
      aria-labelledby={headingId}
      className={cn(
        "rounded-2xl border border-emerald-200 bg-emerald-50 p-5",
        "flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4",
        className
      )}
    >
      <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-700" aria-hidden="true" />
      <p className="min-w-0 flex-1 text-sm font-semibold text-emerald-900">
        <span id={headingId}>{`${tier} starts ${startsOn}, when your trial ends`}</span>.
      </p>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            disabled={cancelScheduled.isPending}
            className="shrink-0 cursor-pointer bg-white border-emerald-300 text-emerald-900 hover:bg-emerald-100 transition-colors duration-200"
          >
            {cancelScheduled.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
            Cancel and stay on trial
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your scheduled subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              {tier} will no longer start on {startsOn}. You stay on your Platinum trial until it ends, then
              move to the Free plan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Keep {tier}</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => cancelScheduled.mutate({ trialEndsAt })}
            >
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
